import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Image, PaintBrush, Trash } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { UploadService } from '../../../../service/UploadService';
import {
  INode,
  ITypeNode,
  useFormBuilder,
} from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { ImageFormData } from '../../schemas/image.schema';
import { imageFormToNode, imageNodeToForm } from '../../utils/nodeDataTransformers';
import { CustomizationForm } from '../FormCustomization/CustomizationForm';

interface ImageSettingsProps {
  node: INode;
}

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
  fileUrl?: string;
  response?: any;
  isExtraction: boolean;
}

export function ImageSettings({ node }: ImageSettingsProps) {
  const { dispatch } = useFormBuilder();
  const [activeTab, setActiveTab] = useState(0);

  const formsMethods = useForm<ImageFormData>({
    defaultValues: imageNodeToForm(node),
  });

  useEffect(() => {
    formsMethods.reset(imageNodeToForm(node));
  }, [node.id, node.data, formsMethods]);

  const onDrop = async (
    acceptedFiles: File[]
  ) => {
    const newFiles = acceptedFiles.map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      name: file.name,
      progress: 0,
      status: 'uploading' as const,
      isExtraction: false,
    }));

    for (const fileData of newFiles) {
      await uploadFile(fileData);
    }
  };

  const uploadFile = async (fileData: UploadedFile) => {
    try {
      const formData = new FormData();
      formData.append('file', fileData.file);

      const response = await UploadService.uploadFile(formData);

      if (response.data.src) {
        formsMethods.setValue('url', response.data.src);
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
    },
    onDrop,
  });

  const handleRemoveImage = () => {
    formsMethods.setValue('url', '');
  };

  useEffect(() => {
    const subscription = formsMethods.watch((data) => {
      if (data && Object.keys(data).length > 0) {
        const updatedData = imageFormToNode(data as ImageFormData, node);
        dispatch({
          type: ActionTypes.UPDATE_NODES_DATA,
          payload: {
            id: node.id,
            updates: { data: updatedData },
          },
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, node.id, dispatch]);

  const formValues = formsMethods.watch();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 16px 0 16px',
        }}
      >
        <PaintBrush size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          Image Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          {activeTab === 0 && (
            <Box sx={{ padding: '0px 16px 0 16px' }}>
              {!formValues?.url ? (
                <Controller
                  name="url"
                  control={formsMethods.control}
                  render={({ field, fieldState }) => (
                    <div
                      style={{
                        border: '1px dashed #667085',
                        borderRadius: '4px',
                        display: 'flex',
                        justifyContent: 'start',
                        alignItems: 'center',
                        color: '#98A2B3',
                        width: '100%',
                        minHeight: '120px',
                      }}
                    >
                      <div
                        {...getRootProps({ className: 'dropzone' })}
                        style={{
                          width: '100%',
                          height: '100%',
                          padding: '16px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          color: '#475467',
                        }}
                      >
                        <input {...getInputProps()} />
                        <Image size={32} style={{ marginBottom: '8px' }} />
                        <p style={{ fontSize: '12px', margin: 0 }}>
                          Drag 'n' drop image here, or click to select
                        </p>
                        <p style={{ fontSize: '10px', marginTop: '4px' }}>
                          JPG, JPEG, PNG
                        </p>
                      </div>
                    </div>
                  )}
                />
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    textAlign: 'center',
                    width: '100%',
                    minHeight: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={formValues.url}
                    alt="Uploaded image"
                    style={{
                      width: '100%',
                      maxHeight: '120px',
                      objectFit: formValues.objectFit || 'cover',
                      borderRadius: '4px',
                      border: '1px solid #E4E7EC',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      },
                    }}
                    onClick={handleRemoveImage}
                  >
                    <Trash size={12} color="white" />
                  </Box>
                </Box>
              )}

              {formValues?.url && (
                <Box sx={{ marginTop: '16px' }}>
                  <Typography
                    style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#101828',
                      marginBottom: '8px',
                    }}
                  >
                    Image Fit
                  </Typography>
                  <FormControl component="fieldset" style={{ width: '100%' }}>
                    <Controller
                      name="objectFit"
                      control={formsMethods.control}
                      render={({ field }) => (
                        <RadioGroup
                          value={field.value || 'cover'}
                          onChange={e =>
                            field.onChange(e.target.value as 'cover' | 'contain')
                          }
                        >
                          <FormControlLabel
                            value="cover"
                            control={<Radio size="small" />}
                            label="Stretch to fill card"
                            style={{ fontSize: '12px' }}
                          />
                          <FormControlLabel
                            value="contain"
                            control={<Radio size="small" />}
                            label="Maintain image proportions"
                            style={{ fontSize: '12px' }}
                          />
                        </RadioGroup>
                      )}
                    />
                  </FormControl>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <CustomizationForm
                control={formsMethods.control}
                type={ITypeNode.IMAGE}
              />
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
